import { drawSoccerField } from './drawField';

/**
 * Draws a formation onto a canvas.
 *
 * Lives beside the ported board rather than in src/lib because it depends on
 * that renderer, and src/lib must stay importable by the strictly-typed test
 * project.
 *
 * One renderer for the screen and the PDF, so what a coach prints is what they
 * arranged. Text is drawn by the browser's own text engine, which shapes Arabic
 * and handles RTL correctly — that is also why the PDF embeds this canvas rather
 * than laying out text in jsPDF, whose core fonts cannot render Arabic at all.
 */

export interface RenderPlacement {
  x: number;
  y: number;
  positionKey?: string | null;
  label?: string | null;
  note?: string | null;
  shirtNumber?: number | null;
  name?: string | null;
  unit?: 'gk' | 'defence' | 'midfield' | 'attack';
}

export interface SheetHeader {
  title: string;
  phaseLabel: string;
  teamName: string;
  coachName: string;
  formationKey: string;
  date: string;
  notes?: string | null;
}

/** Matches the product's intensity ramp, so a unit means the same thing everywhere. */
export const UNIT_FILL: Record<string, string> = {
  gk: '#5b7f9e',
  defence: '#3f8f7f',
  midfield: '#c08a1e',
  attack: '#c24e2b',
};

export const PITCH_PADDING = { top: 0, right: 0, bottom: 0, left: 0 };

/** Where a fractional placement lands on a canvas of this size. */
export function toCanvasPoint(
  p: { x: number; y: number },
  width: number,
  height: number,
  inset = 0.07,
) {
  // Inset so a wide player is not drawn half over the touchline.
  const usableW = width * (1 - inset * 2);
  const usableH = height * (1 - inset * 2);
  return {
    // y is measured from the team's own goal, and the pitch is drawn with that
    // goal at the bottom, so it is flipped here rather than stored inverted.
    cx: width * inset + p.x * usableW,
    cy: height * inset + (1 - p.y) * usableH,
  };
}

export function hitTest(
  placements: readonly RenderPlacement[],
  px: number,
  py: number,
  width: number,
  height: number,
  radius: number,
): number {
  // Last drawn is on top, so search backwards.
  for (let i = placements.length - 1; i >= 0; i--) {
    const { cx, cy } = toCanvasPoint(placements[i]!, width, height);
    if ((px - cx) ** 2 + (py - cy) ** 2 <= radius ** 2) return i;
  }
  return -1;
}

export function tokenRadius(width: number): number {
  return Math.max(14, Math.min(26, width * 0.028));
}

/**
 * Draws the pitch upright, with the team's own goal at the bottom.
 *
 * The ported renderer draws a landscape pitch with the goals left and right,
 * which is right for a drill diagram and wrong for a formation: a team sheet is
 * read with the keeper at the bottom and the attack at the top. So the pitch is
 * rendered landscape offscreen and rotated a quarter turn, which also means a
 * phone — held portrait — shows the shape at its largest.
 */
export function drawPortraitPitch(ctx: CanvasRenderingContext2D, width: number, height: number) {
  const off = document.createElement('canvas');
  // Swapped: the landscape pitch is as long as the portrait one is tall.
  off.width = height;
  off.height = width;
  const offCtx = off.getContext('2d');
  if (!offCtx) return;
  drawSoccerField(offCtx, height, width, 'full');

  ctx.save();
  // A quarter turn anticlockwise puts the pitch's left goal at the bottom.
  ctx.translate(0, height);
  ctx.rotate(-Math.PI / 2);
  ctx.drawImage(off, 0, 0);
  ctx.restore();
}

export function drawFormation(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  placements: readonly RenderPlacement[],
  options: { selectedIndex?: number; showNotes?: boolean } = {},
) {
  drawPortraitPitch(ctx, width, height);

  const r = tokenRadius(width);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  placements.forEach((p, i) => {
    const { cx, cy } = toCanvasPoint(p, width, height);
    const selected = options.selectedIndex === i;

    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = p.unit ? (UNIT_FILL[p.unit] ?? '#46566b') : '#46566b';
    ctx.fill();
    ctx.restore();

    ctx.lineWidth = selected ? 3 : 2;
    ctx.strokeStyle = selected ? '#e0a500' : 'rgba(255,255,255,0.9)';
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();

    // Shirt number if picked, otherwise the position code: an empty slot still
    // has to say what it is.
    const inner = p.shirtNumber != null ? String(p.shirtNumber) : (p.positionKey ?? '');
    ctx.fillStyle = '#ffffff';
    ctx.font = `600 ${Math.round(r * (inner.length > 2 ? 0.72 : 0.95))}px "IBM Plex Sans Condensed", sans-serif`;
    ctx.fillText(inner, cx, cy + 1);

    const caption = p.name ?? p.label ?? p.positionKey ?? '';
    if (caption) {
      ctx.font = `500 ${Math.round(r * 0.66)}px "IBM Plex Sans", sans-serif`;
      const textY = cy + r + Math.round(r * 0.78);
      const w = ctx.measureText(caption).width;
      ctx.fillStyle = 'rgba(16,26,40,0.72)';
      const padX = 5;
      const boxH = Math.round(r * 0.92);
      ctx.beginPath();
      ctx.roundRect(cx - w / 2 - padX, textY - boxH / 2, w + padX * 2, boxH, 3);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.fillText(caption, cx, textY);
    }

    if (options.showNotes && p.note) {
      ctx.font = `400 ${Math.round(r * 0.58)}px "IBM Plex Sans", sans-serif`;
      ctx.fillStyle = 'rgba(255,255,255,0.92)';
      ctx.fillText(p.note, cx, cy + r + Math.round(r * 1.7));
    }
  });
}

/**
 * Draws the whole printable sheet: a header block, the pitch, and a footer.
 *
 * Everything a printed team sheet has to carry — who, which team, which phase,
 * which shape, and when — so a page pinned to a dressing-room wall still makes
 * sense a month later.
 */
export function drawSheet(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  placements: readonly RenderPlacement[],
  header: SheetHeader,
  dir: 'ltr' | 'rtl' = 'ltr',
) {
  const pad = Math.round(width * 0.055);
  const headerH = Math.round(height * 0.105);
  const footerH = Math.round(height * 0.04);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  const start = dir === 'rtl' ? width - pad : pad;
  const end = dir === 'rtl' ? pad : width - pad;
  ctx.textAlign = dir === 'rtl' ? 'right' : 'left';
  ctx.textBaseline = 'alphabetic';

  ctx.fillStyle = '#101a28';
  ctx.font = `600 ${Math.round(headerH * 0.36)}px "IBM Plex Sans Condensed", sans-serif`;
  ctx.fillText(header.title, start, Math.round(headerH * 0.45));

  ctx.fillStyle = '#46566b';
  ctx.font = `500 ${Math.round(headerH * 0.24)}px "IBM Plex Sans", sans-serif`;
  ctx.fillText(header.phaseLabel, start, Math.round(headerH * 0.75));

  // Meta on the opposite edge, so the two blocks never collide on a long title.
  ctx.textAlign = dir === 'rtl' ? 'left' : 'right';
  ctx.fillStyle = '#46566b';
  ctx.font = `400 ${Math.round(headerH * 0.2)}px "IBM Plex Sans", sans-serif`;
  const meta = [header.teamName, header.coachName, header.formationKey, header.date];
  meta.forEach((line, i) => {
    ctx.fillText(line, end, Math.round(headerH * 0.3) + i * Math.round(headerH * 0.24));
  });

  ctx.strokeStyle = '#d5dce4';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(pad, headerH);
  ctx.lineTo(width - pad, headerH);
  ctx.stroke();

  const pitchTop = headerH + Math.round(pad * 0.6);
  const pitchH = height - pitchTop - footerH - pad;
  const pitchW = width - pad * 2;

  ctx.save();
  ctx.translate(pad, pitchTop);
  const off = document.createElement('canvas');
  off.width = pitchW;
  off.height = pitchH;
  const offCtx = off.getContext('2d');
  if (offCtx) {
    drawFormation(offCtx, pitchW, pitchH, placements, { showNotes: true });
    ctx.drawImage(off, 0, 0);
  }
  ctx.restore();

  ctx.textAlign = dir === 'rtl' ? 'right' : 'left';
  ctx.fillStyle = '#7c8a9c';
  ctx.font = `400 ${Math.round(footerH * 0.38)}px "IBM Plex Sans", sans-serif`;
  if (header.notes) {
    ctx.fillText(header.notes.slice(0, 160), start, height - Math.round(footerH * 0.5));
  }
  ctx.textAlign = dir === 'rtl' ? 'left' : 'right';
  ctx.fillText('MyPlannerPad', end, height - Math.round(footerH * 0.5));
}
